// Playback transport (IMPLEMENTATION_PLAN.md section 6, ADR 0004).
//
// Position model: the engine maps output time (when sound leaves it) to input time piecewise-
// linearly. Every change is scheduled `lead` seconds ahead with an explicit input position, and
// this class keeps the same map, so position never depends on messages from the audio thread.

export interface TimeSource {
  readonly currentTime: number;
  /** Hardware output delay (seconds); not reported by every browser. */
  readonly outputLatency?: number;
  readonly baseLatency?: number;
}

export interface EngineChange {
  /** Context time at which the change takes effect. */
  output: number;
  active: boolean;
  /** Song position (seconds) at `output`. */
  input: number;
  rate: number;
  semitones: number;
  /** Loop region; disabled when loopStart === loopEnd. */
  loopStart: number;
  loopEnd: number;
}

export interface PlaybackEngine {
  /** How far ahead changes must be scheduled (engine latency plus a margin). */
  readonly lead: number;
  schedule(change: EngineChange): void;
  setVolume(gain: number): void;
  dispose(): void;
}

export interface LoopRange {
  start: number;
  end: number;
}

export type TransportState = 'paused' | 'playing';
/** 'change': any new schedule (play, pause, seek, rate, loop, pitch), for sound schedulers. */
export type TransportEvent = 'state' | 'seek' | 'rate' | 'loop' | 'volume' | 'ended' | 'change';

export interface TimerApi {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const defaultTimers: TimerApi = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => {
    globalThis.clearTimeout(h as ReturnType<typeof setTimeout>);
  },
};

export const MIN_RATE = 0.5;
export const MAX_RATE = 1.5;

/**
 * What the player and the views use to control and follow playback. Implemented by Transport
 * (the song's own audio) and by the YouTube module's transport (an embedded video).
 */
export interface PlayerTransport {
  readonly duration: number;
  readonly state: TransportState;
  readonly rate: number;
  readonly loop: LoopRange | null;
  readonly volume: number;
  readonly muted: boolean;
  /** Speeds this transport can play at; undefined = any between MIN_RATE and MAX_RATE. */
  readonly rateSteps?: readonly number[];
  /** What the listener hears now, in song seconds. */
  getPosition(visualOffsetMs?: number): number;
  play(): void;
  pause(): void;
  toggle(): void;
  seek(seconds: number): void;
  setRate(rate: number): void;
  setLoop(loop: LoopRange | null): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
  on(event: TransportEvent, listener: () => void): () => void;
  dispose(): void;
}

export class Transport implements PlayerTransport {
  private playing = false;
  private rateValue = 1;
  private semitonesValue = 0;
  private loopValue: LoopRange | null = null;
  private volumeValue = 1;
  private mutedValue = false;
  /** Context time where the current segment starts, and the song position there. */
  private anchorOutput = 0;
  private anchorInput = 0;
  private endTimer: unknown = null;
  private readonly listeners = new Map<TransportEvent, Set<() => void>>();

  constructor(
    private readonly clock: TimeSource,
    private readonly engine: PlaybackEngine,
    readonly duration: number,
    private readonly timers: TimerApi = defaultTimers,
  ) {}

  get state(): TransportState {
    return this.playing ? 'playing' : 'paused';
  }
  get rate(): number {
    return this.rateValue;
  }
  get semitones(): number {
    return this.semitonesValue;
  }
  get loop(): LoopRange | null {
    return this.loopValue;
  }
  get volume(): number {
    return this.volumeValue;
  }
  get muted(): boolean {
    return this.mutedValue;
  }

  /** Where the current schedule starts: context time and song position there. */
  get anchor(): { output: number; input: number } {
    return { output: this.anchorOutput, input: this.anchorInput };
  }

  /**
   * Song position at `outputTime` without folding loops back (it keeps growing past the loop end),
   * or null while paused. Sound schedulers map events through this.
   */
  unwrappedAt(outputTime: number): number | null {
    if (!this.playing) return null;
    return this.anchorInput + Math.max(0, outputTime - this.anchorOutput) * this.rateValue;
  }

  /** Context time at which unwrapped song position `input` is heard by the engine output. */
  outputTimeOf(unwrapped: number): number {
    return this.anchorOutput + (unwrapped - this.anchorInput) / this.rateValue;
  }

  /** Song position (seconds) that leaves the engine at context time `outputTime`. */
  positionAt(outputTime: number): number {
    if (!this.playing || outputTime <= this.anchorOutput) return this.anchorInput;
    let input = this.anchorInput + (outputTime - this.anchorOutput) * this.rateValue;
    const loop = this.loopValue;
    if (loop && loop.end > loop.start && input >= loop.end) {
      input = loop.start + ((input - loop.start) % (loop.end - loop.start));
    }
    return Math.min(input, this.duration);
  }

  /**
   * What the listener hears now: compensates the hardware output delay, plus an optional
   * user calibration offset (Settings → sync offset) for devices that misreport it.
   */
  getPosition(visualOffsetMs = 0): number {
    // Some browsers report outputLatency as 0 (unknown); fall back to baseLatency then.
    const reported = this.clock.outputLatency ?? 0;
    const hw = reported > 0 ? reported : (this.clock.baseLatency ?? 0);
    return this.positionAt(this.clock.currentTime - hw - visualOffsetMs / 1000);
  }

  /** Starts playback, at context time `at` if given (count-in), else as soon as possible. */
  play(at?: number): void {
    if (this.playing) return;
    if (this.anchorInput >= this.duration - 1e-3) this.anchorInput = this.loopValue?.start ?? 0;
    this.anchorOutput = Math.max(this.clock.currentTime + this.engine.lead, at ?? 0);
    this.playing = true;
    this.push();
    this.emit('state');
  }

  pause(): void {
    if (!this.playing) return;
    this.reanchor();
    this.playing = false;
    this.push();
    this.emit('state');
  }

  toggle(): void {
    if (this.playing) this.pause();
    else this.play();
  }

  /** Jumps to `seconds`. While a loop is on, a target outside it goes to the loop start. */
  seek(seconds: number): void {
    this.reanchor();
    let target = Math.max(0, Math.min(seconds, this.duration));
    const loop = this.loopValue;
    if (loop && (target < loop.start || target >= loop.end)) target = loop.start;
    this.anchorInput = target;
    this.push();
    this.emit('seek');
  }

  /** Playback speed with pitch kept (F3.2), clamped to 50–150%. */
  setRate(rate: number): void {
    const next = Math.max(MIN_RATE, Math.min(MAX_RATE, rate));
    if (next === this.rateValue) return;
    this.reanchor();
    this.rateValue = next;
    this.push();
    this.emit('rate');
  }

  /** Pitch shift in semitones (F3.3, V1). */
  setSemitones(semitones: number): void {
    this.reanchor();
    this.semitonesValue = semitones;
    this.push();
  }

  /** A–B loop (F3.4). If playback is outside the new loop, it jumps to the loop start. */
  setLoop(loop: LoopRange | null): void {
    this.reanchor();
    if (loop && loop.end > loop.start) {
      const range = { start: Math.max(0, loop.start), end: Math.min(this.duration, loop.end) };
      this.loopValue = range;
      if (this.anchorInput < range.start || this.anchorInput >= range.end) {
        this.anchorInput = range.start;
      }
    } else {
      this.loopValue = null;
    }
    this.push();
    this.emit('loop');
  }

  setVolume(volume: number): void {
    this.volumeValue = Math.max(0, Math.min(1, volume));
    this.engine.setVolume(this.mutedValue ? 0 : this.volumeValue);
    this.emit('volume');
  }

  setMuted(muted: boolean): void {
    this.mutedValue = muted;
    this.engine.setVolume(muted ? 0 : this.volumeValue);
    this.emit('volume');
  }

  on(event: TransportEvent, listener: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => set.delete(listener);
  }

  dispose(): void {
    this.timers.clearTimeout(this.endTimer);
    this.listeners.clear();
    this.engine.dispose();
  }

  /** Moves the anchor to the earliest time a new change can take effect. */
  private reanchor(): void {
    const when = this.clock.currentTime + this.engine.lead;
    this.anchorInput = this.positionAt(when);
    this.anchorOutput = when;
  }

  private push(): void {
    this.engine.schedule({
      output: this.anchorOutput,
      active: this.playing,
      input: this.anchorInput,
      rate: this.rateValue,
      semitones: this.semitonesValue,
      loopStart: this.loopValue?.start ?? 0,
      loopEnd: this.loopValue?.end ?? 0,
    });
    this.scheduleEndCheck();
    this.emit('change');
  }

  /** Without a loop, playback stops at the end of the song. */
  private scheduleEndCheck(): void {
    this.timers.clearTimeout(this.endTimer);
    this.endTimer = null;
    if (!this.playing || this.loopValue) return;
    const endOutput = this.anchorOutput + (this.duration - this.anchorInput) / this.rateValue;
    const ms = Math.max(0, (endOutput - this.clock.currentTime) * 1000) + 30;
    this.endTimer = this.timers.setTimeout(() => {
      if (!this.playing || this.loopValue) return;
      if (this.positionAt(this.clock.currentTime) < this.duration - 0.02) {
        this.scheduleEndCheck();
        return;
      }
      this.playing = false;
      this.anchorInput = this.duration;
      this.anchorOutput = this.clock.currentTime;
      this.push();
      this.emit('state');
      this.emit('ended');
    }, ms);
  }

  private emit(event: TransportEvent): void {
    for (const listener of this.listeners.get(event) ?? []) listener();
  }
}
